// Transitions PR-4b — the honest execute-extension confirm for a Scheduled
// BMSP maturity extension. Amendment-grade: staff pick the granted years and
// confirm the plain-language consequences (maturity moves, 1% fee, the rate
// never changes) before the atomic amendment + fee run.

import { useState } from "react";
import type { FormEvent } from "react";
import { Button, Label, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { useExecuteBmspExtension } from "@/hooks/portfolioAdmin/useLoanTransitions";
import type { LoanTransitionDto } from "@/api/portfolioAdmin/loanTransitionsApi";

/** Every consequence, in plain words — shown before the confirm. */
const CONSEQUENCES: readonly string[] = [
  "The loan's maturity date and term move forward by the granted years — the note is amended, not corrected.",
  "The 1.00% extension fee (on outstanding principal) is collected now through the waterfall.",
  "The interest rate stays at the original 10.00% — an extension never reprices the note.",
  "Monthly payments continue unchanged until the new balloon maturity.",
  "The BLA allows at most two extensions — a third will be refused.",
];

export function ExecuteBmspExtensionModal({
  loanId,
  transition,
  open,
  onClose,
}: {
  loanId: string;
  transition: LoanTransitionDto;
  open: boolean;
  onClose: () => void;
}) {
  const mutation = useExecuteBmspExtension(loanId);
  const [years, setYears] = useState(1);
  const [note, setNote] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await mutation.mutateAsync({
        transitionId: transition.id,
        body: { extensionYears: years, note: note.trim() || null },
      });
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Execute maturity extension"
      description="The note-holder-approved BMSP extension: the loan's maturity is amended and the extension fee is collected now. This cannot be undone here."
    >
      <form
        onSubmit={submit}
        className="flex flex-col gap-space-3"
        data-testid="execute-bmsp-extension-form"
      >
        <div>
          <Label htmlFor="extension-years">Extension granted</Label>
          <select
            id="extension-years"
            className="w-full rounded border border-border-light bg-white-pure px-space-2 py-space-1 text-small"
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            data-testid="extension-years-select"
          >
            {[1, 2, 3, 4, 5].map((y) => (
              <option key={y} value={y}>
                {y} year{y === 1 ? "" : "s"}
              </option>
            ))}
          </select>
        </div>

        <div className="rounded border border-border-light bg-white-warm p-space-3">
          <p className="text-small font-semibold m-0 mb-space-1">What happens when you execute:</p>
          <ul
            className="m-0 pl-space-4 flex flex-col gap-space-1"
            data-testid="extension-consequences"
          >
            {CONSEQUENCES.map((c) => (
              <li key={c} className="text-small">
                {c}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <Label htmlFor="extension-note">Note (optional)</Label>
          <Textarea
            id="extension-note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <label className="flex items-start gap-space-2 text-small">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="extension-acknowledge"
          />
          <span>
            I understand the note is amended and the extension fee is collected now.
          </span>
        </label>

        {error && (
          <p
            className="text-small text-status-error m-0"
            role="alert"
            data-testid="execute-bmsp-extension-error"
          >
            {error}
          </p>
        )}
        <div className="flex justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={mutation.isPending || !acknowledged}
            data-testid="execute-bmsp-extension-submit"
          >
            {mutation.isPending ? "Executing…" : "Execute extension"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
